import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { InputTitleComponent, PopupRef, UInputComponent, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { HighlightStyle } from '@shared/pipes';
import { isRightClick } from '@shared/utils';
import { stopEvent } from '@shared/utils/elements-util';
import { WikisService } from 'src/app/bar/services/wikis/wikis.service';
import { InvokeCommand } from '../../models/invoke-command.model';
import { FolderItem } from '../../models/results-types';

@UntilDestroy()
@Component({
  selector: 'result-folder',
  templateUrl: './result-folder.component.html',
  styleUrls: ['./result-folder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultFolderComponent {
  readonly FOLDER_ICON: UiIconModel = { type: 'font', value: 'icon-folder-full' };
  resultInputTitle: UInputComponent;
  contextMenuActive: PopupRef<ContextMenuComponent, ContextMenuData>;
  highlightStyle: HighlightStyle = 'default';

  @Input() model: FolderItem;
  @Input() inputPlaceholder: string;
  @Input() defaultInputModel: string;
  @Input() descriptionTextCount: string;
  @Input() showInputTitle: boolean;
  @Input() canEdit: boolean;

  @Output() titleChanged = new EventEmitter<string>();
  @Output() invoke = new EventEmitter<InvokeCommand>();
  @Output() onBlur = new EventEmitter();
  @Output() contextOpen = new EventEmitter();
  @Output() contextClose = new EventEmitter();

  @ViewChild(InputTitleComponent) inputTitleComponent: InputTitleComponent;

  constructor(private cdr: ChangeDetectorRef, private contextMenuService: ContextMenuService, private wikisService: WikisService) {}

  auxclick(event: PointerEvent) {
    if (!isRightClick(event)) return;
    stopEvent(event);
    if (this.canEdit) {
      this.openContextMenu(event);
    }
  }

  openContextMenu(event: { x: number; y: number }) {
    const items = this.buildContextMenuItems();
    this.contextMenuActive = this.contextMenuService.open(
      event,
      {
        items,
        onInvoke: (item: ContextMenuItem) => {
          this.invoke.emit({ command: item.command, invokerItem: this.model });
          this.contextClose.emit();
        },
      },
      { position: 'below' }
    );
    this.contextMenuActive.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuActive = null;
      this.contextClose.emit();
      this.cdr.markForCheck();
    });
    this.contextOpen.emit();
  }

  private buildContextMenuItems(): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];

    const validateLimitFolder = this.model.path?.length < this.wikisService.FOLDER_LIMIT;

    items.push(
      {
        id: 'copy-clipboard',
        text: 'Copy URL',
        icon: { type: 'font-icon', value: 'icon-link2' },
        data: {},
        command: { type: 'copy-clipboard' },
      },
      {
        id: 'move-folder',
        text: 'Move to...',
        icon: { type: 'font-icon', value: 'icon-exit' },
        command: { type: 'move-folder' }, //move-to-collection
      },
      {
        id: 'create-card',
        text: 'Create new card',
        icon: { type: 'font-icon', value: 'icon-card' },
        command: { type: 'create-card' },
      },
      {
        id: 'create-folder',
        text: validateLimitFolder ? 'Create new folder' : 'Folder limit reached',
        icon: { type: 'font-icon', value: 'icon-folder' },
        command: { type: 'create-folder' },
        disabled: !validateLimitFolder,
      },
      {
        id: 'rename-folder',
        text: 'Rename',
        icon: { type: 'font-icon', value: 'icon-edit-frame' },
        command: { type: 'rename-folder' },
      },
      {
        id: 'delete-folder',
        text: 'Delete',
        icon: { type: 'font-icon', value: 'icon-delete' },
        command: { type: 'delete-folder' },
      }
    );

    return items;
  }

  onEditTitleClickOut() {
    if (this.resultInputTitle && this.resultInputTitle.model !== this.model?.title) {
      this.onBlurUInput({
        inputElement: this.inputTitleComponent.resultInputTitle,
        defaultInputModel: this.inputTitleComponent.defaultInputModel,
      });
    } else {
      this.showInputTitle = false;
      this.onBlur.emit();
      this.cdr.markForCheck();
    }
  }

  onBlurUInput(event) {
    const { inputElement, defaultValue } = event;
    this.resultInputTitle = inputElement;
    const noValueFromInput = !inputElement?.model || !inputElement?.model.length;
    const newTitle = defaultValue && noValueFromInput ? defaultValue : inputElement?.model;
    this.titleChanged.emit(newTitle);
    this.onBlur.emit();
    this.showInputTitle = false;
    this.model.title = newTitle;
    this.cdr.markForCheck();
  }

  onDisplayInput(event) {
    this.resultInputTitle = event;
    this.cdr.markForCheck();
  }
}
